import React, { Component, Fragment } from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { Allergens } from './Allergens'
import { NutritionalValues } from './NutritionalValues'
import { Ingredients } from './Ingredients'
import { media, ResponsiveContainer, style } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import styled, { css } from 'styled-components'
import { FooterNotice } from 'app/common/footer-notice'
import { FeaturedProducts } from 'app/common/featured-products'
import { H1, H6, SmallText } from 'app/common/typography'
import { Breadcrumb } from 'app/common/breadcrumb'
import flame_grilled from 'shared/assets/images/flame-grilled.png'
import msc from 'shared/assets/images/msc.png'
import { Chevron, HandIcon } from 'app/assets/icons'
import { Radio } from './Radio'
import type {
    BurgerIngredientImageDTO,
    CategoryInfoDTO,
    FilterShortDTO,
    IngredientShortDTO,
    MarketingAnimationShortDTO,
    MenuShortDTO,
    NutritionShortDTO,
    ProductShortDTO,
    SuggestionShortDTO
} from 'shared/types'
import type { ECommerceMetadata, EventMetadata } from 'shared/store/metadata'
import { eventsDefinitions } from 'shared/store/metadata'
import { Link } from 'app/common/link'
import { Placeholder } from 'app/common/placeholder'
import type { MenuElement, MenuSection } from 'shared/store/product'
import { MenuType, productTypes } from 'shared/store/product'
import { TooltipPop } from 'app/common/tooltip-pop'
import { Picture } from 'app/common/picture'
import {
    categoryId,
    displayMeatOrigin,
    featureCodes,
    productFilters
} from 'shared/utils'
import { CcCartCheck } from 'app/ecommerce/click-and-collect/cart-check'
import { FeatureToggle } from 'app/common/feature-toggle'
import { IconButton } from 'app-v2/design-systems/components/inputs/button'
import { Icon } from 'app-v2/shared/features/icon'
import { tokens } from 'app-v2/shared/features/icon/utils/tokens'
import { MeatOrigin } from './MeatOrigin'
import { BORDER, SPACING } from 'app-v2/design-tokens'
import { Spacer } from 'app-v2/design-systems'
import { isMobile, isTablet } from 'react-device-detect'

const { tertiary2, grey30, red, red2, shadow, tertiary, lightMayo } = colors

type Props = {
    location: Object,
    handleElementClick: (MenuElement, MenuSection) => void,
    handleTabClick: MenuSection => void,
    fetchProductPageData: (string, ?boolean) => void,
    resetProductPageData: () => void,
    sendEventMeta: EventMetadata => void,
    sendECommerceMeta: ECommerceMetadata => void,
    category: CategoryInfoDTO,
    description: string,
    image: Array<string>,
    footerNotice: MarketingAnimationShortDTO,
    sections: Array<MenuSection>,
    menu: MenuShortDTO,
    product: ProductShortDTO,
    ingredients: Array<IngredientShortDTO>,
    noAllergens: boolean,
    allergens: Array<FilterShortDTO>,
    traces: Array<FilterShortDTO>,
    nutrition: Array<NutritionShortDTO>,
    burgerImages: Array<BurgerIngredientImageDTO>,
    suggestions: SuggestionShortDTO,
    title: string,
    type: string,
    intl: IntlShape,
    history: Object,
    match: any,
    fromEcommerce?: boolean,
    productPath?: string
}

class ProductSheet extends Component<Props> {
    handleElementClick = (
        e: Event,
        element: MenuElement,
        section: MenuSection
    ) => {
        const { handleElementClick } = this.props
        e.preventDefault()
        handleElementClick(element, section)
    }

    handleTabClick = (e: Event, section: MenuSection) => {
        const { handleTabClick } = this.props
        e.preventDefault()
        handleTabClick(section)
    }

    componentDidMount() {
        const { fetchProductPageData, productPath, fromEcommerce } = this.props
        if (productPath) {
            fetchProductPageData(productPath, !fromEcommerce)
        }
    }

    componentWillUnmount() {
        const { fromEcommerce, resetProductPageData } = this.props
        if (fromEcommerce) {
            resetProductPageData()
        }
    }

    handleBackClick = (e: Event) => {
        e.preventDefault()
        this.props.history.goBack()
    }

    menuSectionTitle(type: number) {
        switch (type) {
            case MenuType.DRINK:
                return 'product.menu.drinks'
            case MenuType.MAIN:
                return 'product.menu.burgers'
            case MenuType.SIDE:
                return 'product.menu.sides'
            default:
            case MenuType.OTHERS:
                if (this.productType() === productTypes.KIDS) {
                    return 'product.menu.desserts'
                } else {
                    return 'product.menu.others'
                }
        }
    }

    getSelectedProduct(type: number) {
        const { sections } = this.props

        let image = []
        sections.map(section => {
            if (section.type === type) {
                section.elements.map(element => {
                    return element.selected ? (image = element.image) : false
                })
            }
            return false
        })
        return image
    }

    productType() {
        switch (this.props.type.toLowerCase()) {
            case 'menu':
            case 'breakfast':
            case 'king_deal':
                return productTypes.MENU
            case 'kids':
                return productTypes.KIDS
            case 'multi_dishes':
                return productTypes.MULTI_DISHES
            case 'snack':
                return productTypes.SNACK
            default:
                return productTypes.BURGER
        }
    }

    renderLogo() {
        const { product } = this.props
        if (product && this.productType() === productTypes.BURGER) {
            if (product.flameGrilled) {
                return <FlameImage src={flame_grilled} />
            } else if (product.msc) {
                return <FlameImage src={msc} />
            }
        }
    }

    renderIngredients() {
        const { ingredients, burgerImages } = this.props

        return (
            this.productType() !== productTypes.SNACK &&
            this.productType() !== productTypes.KIDS &&
            this.productType() !== productTypes.MULTI_DISHES &&
            ingredients &&
            ingredients.length > 0 && (
                <Wrapper>
                    <Ingredients
                        ingredients={ingredients}
                        burgerImages={burgerImages}
                    />
                </Wrapper>
            )
        )
    }

    renderProductInformation(
        description: string,
        noAllergens: boolean,
        allergens: Array<FilterShortDTO>,
        traces: Array<FilterShortDTO>,
        nutrition: Array<NutritionShortDTO>
    ) {
        const { filters, category } = this.props

        return (
            <Fragment>
                <StyledText>{description}</StyledText>

                <Divider />

                <Allergens
                    noAllergens={noAllergens}
                    allergens={allergens}
                    traces={traces}
                    sendMeta={this.props.sendEventMeta}
                />

                <Divider />

                {nutrition && nutrition.length > 0 && (
                    <NutritionalValues
                        nutritions={nutrition}
                        sendMeta={this.props.sendEventMeta}
                        isMenu={[
                            productTypes.MENU,
                            productTypes.MULTI_DISHES,
                            productTypes.KIDS
                        ].includes(this.productType())}
                    />
                )}

                <FeatureToggle feature={featureCodes.MEAT_ORIGIN}>
                    {displayMeatOrigin(filters, category) && (
                        <>
                            <Divider />
                            <MeatOrigin />
                        </>
                    )}
                </FeatureToggle>
            </Fragment>
        )
    }

    renderMobileSectionElement(element: MenuElement) {
        return (
            <SectionElementMobile>
                <Picture singleImage imageUrl={element.image} />
            </SectionElementMobile>
        )
    }

    renderSectionElement(element: MenuElement) {
        return (
            <SectionElement>
                <Picture singleImage imageUrl={element.image} />
            </SectionElement>
        )
    }

    renderProductImage() {
        const { menu, product } = this.props

        if (this.productType() === productTypes.MENU) {
            const { sides, mains, drinks } = menu

            return (
                <Fragment>
                    {sides && (
                        <MenuLeftImage>
                            <Picture
                                singleImage
                                imageUrl={this.getSelectedProduct(
                                    MenuType.SIDE
                                )}
                            />
                        </MenuLeftImage>
                    )}
                    {mains && (
                        <MenuMainImage>
                            <Picture singleImage imageUrl={mains[0].image} />
                        </MenuMainImage>
                    )}
                    {drinks && (
                        <MenuRightImage>
                            <Picture
                                singleImage
                                imageUrl={this.getSelectedProduct(
                                    MenuType.DRINK
                                )}
                            />
                        </MenuRightImage>
                    )}
                </Fragment>
            )
        } else if (
            this.productType() === productTypes.KIDS ||
            this.productType() === productTypes.MULTI_DISHES
        ) {
            return (
                <ProductImage>
                    <Picture singleImage imageUrl={menu.image} />
                </ProductImage>
            )
        } else {
            return (
                <Fragment>
                    {this.renderLogo()}
                    {product && (
                        <ProductImage>
                            <Picture singleImage imageUrl={product.image} />
                        </ProductImage>
                    )}
                </Fragment>
            )
        }
    }

    sectionTitle(section: MenuSection, intl: IntlShape) {
        return (
            <H6>
                {intl.formatMessage({
                    id: this.menuSectionTitle(section.type)
                })}
            </H6>
        )
    }

    getPlaceholderMinHeight = (
        coeffMenu: number,
        coeffProduct: number,
        width: string,
        propName: string
    ) => ({
        [propName]:
            this.productType() === productTypes.MENU
                ? `calc( ${width} * ${coeffMenu})`
                : `calc( ${width} * ${coeffProduct})`
    })

    render() {
        const {
            category,
            description,
            footerNotice,
            suggestions,
            title,
            noAllergens,
            allergens,
            traces,
            nutrition,
            sections,
            intl,
            product,
            fromEcommerce
        } = this.props

        const { categoryId = '', title: categoryTitle = '' } = category || {}

        const meta = eventsDefinitions['RESTAURANT_SEARCH_PRODUCT']

        const catalogRoute = intl.formatMessage({
            id: 'routes.catalog'
        })

        const productPageRoute = intl.formatMessage({
            id: 'routes.custom'
        })

        const productPageCustom = product && product.customPageRouteId

        const minHeightDesktop = this.getPlaceholderMinHeight(
            0.37,
            0.6,
            '(100vw - 40px)',
            'minHeightDesktop'
        )

        const minHeightDesktopLarge = this.getPlaceholderMinHeight(
            0.37,
            0.6,
            '1280px ',
            'minHeightDesktopLarge'
        )

        const minHeightMobile = this.getPlaceholderMinHeight(
            0.5,
            1.03,
            '(100vw - 40px)',
            'minHeightMobile'
        )

        return (
            <MainContainer>
                <Wrapper>
                    <ContainerWrapper fromEcommerce={fromEcommerce}>
                        {!fromEcommerce && (
                            <Breadcrumb
                                breadcrumbArray={[
                                    {
                                        link: catalogRoute,
                                        title: intl.formatMessage({
                                            id:
                                                'catalog.card.title.breadcrumb.end'
                                        })
                                    },
                                    {
                                        link: `${catalogRoute}/${categoryId}`,
                                        title: `\u00A0>\u00A0${categoryTitle}`
                                    },
                                    {
                                        title: `\u00A0>\u00A0${title}`
                                    }
                                ]}
                            />
                        )}
                        <Placeholder
                            {...minHeightDesktop}
                            {...minHeightDesktopLarge}>
                            <Container>
                                <LeftBlockContainer>
                                    <LeftBlockMobile>
                                        {!fromEcommerce && (
                                            <StyledAnchor
                                                onClick={e =>
                                                    this.handleBackClick(e)
                                                }>
                                                <StyledArrow>
                                                    <Chevron direction="left" />
                                                </StyledArrow>
                                            </StyledAnchor>
                                        )}
                                        <StyledH1>{title}</StyledH1>
                                        <Placeholder {...minHeightMobile}>
                                            <MenuImageContainer>
                                                {this.renderProductImage()}
                                            </MenuImageContainer>
                                        </Placeholder>
                                    </LeftBlockMobile>
                                    <LeftBlockMobile>
                                        {sections.map(
                                            (section, index) =>
                                                !(
                                                    this.productType() !==
                                                        productTypes.KIDS &&
                                                    this.productType() !==
                                                        productTypes.MULTI_DISHES &&
                                                    section.type >=
                                                        MenuType.MAIN
                                                ) && (
                                                    <MobileMenu key={index}>
                                                        <MenuTitleContainer>
                                                            {this.sectionTitle(
                                                                section,
                                                                intl
                                                            )}
                                                        </MenuTitleContainer>
                                                        <MobileMenuContainer
                                                            key={index}>
                                                            {section.elements.map(
                                                                (
                                                                    element: MenuElement,
                                                                    index
                                                                ) => (
                                                                    <TooltipPop
                                                                        key={
                                                                            index
                                                                        }
                                                                        label={
                                                                            element.name
                                                                        }>
                                                                        {this.productType() ===
                                                                            productTypes.KIDS ||
                                                                        this.productType() ===
                                                                            productTypes.MULTI_DISHES ? (
                                                                            <SectionElementContainer>
                                                                                {this.renderMobileSectionElement(
                                                                                    element
                                                                                )}
                                                                            </SectionElementContainer>
                                                                        ) : (
                                                                            <StyledAnchor
                                                                                key={
                                                                                    index
                                                                                }
                                                                                onClick={e =>
                                                                                    this.handleElementClick(
                                                                                        e,
                                                                                        element,
                                                                                        section
                                                                                    )
                                                                                }>
                                                                                <SectionElementContainer>
                                                                                    {this.renderMobileSectionElement(
                                                                                        element
                                                                                    )}
                                                                                    <StyledRadio>
                                                                                        <Radio
                                                                                            checked={
                                                                                                element.selected
                                                                                            }
                                                                                        />
                                                                                    </StyledRadio>
                                                                                </SectionElementContainer>
                                                                            </StyledAnchor>
                                                                        )}
                                                                    </TooltipPop>
                                                                )
                                                            )}
                                                        </MobileMenuContainer>
                                                    </MobileMenu>
                                                )
                                        )}
                                        {this.renderProductInformation(
                                            description,
                                            noAllergens,
                                            allergens,
                                            traces,
                                            nutrition
                                        )}
                                    </LeftBlockMobile>
                                    <LeftBlock>
                                        <BottomBorder>
                                            <StyledH1>{title}</StyledH1>
                                        </BottomBorder>
                                        {this.renderProductInformation(
                                            description,
                                            noAllergens,
                                            allergens,
                                            traces,
                                            nutrition
                                        )}
                                    </LeftBlock>
                                </LeftBlockContainer>
                                <MainBlocContainer>
                                    {this.renderProductImage()}
                                </MainBlocContainer>
                                <RightBlocContainer>
                                    {productPageCustom && (
                                        <FormattedMessage id="product.sheet.learn.more">
                                            {(text: string) => (
                                                <StyledLink
                                                    url={`${productPageRoute}/${productPageCustom}`}
                                                    background={red2}>
                                                    {text}
                                                </StyledLink>
                                            )}
                                        </FormattedMessage>
                                    )}
                                    {!fromEcommerce && (
                                        <FeatureToggle
                                            feature={
                                                featureCodes.CLICK_AND_COLLECT
                                            }>
                                            <RightBlock>
                                                <FormattedMessage id="product.sheet.get">
                                                    {(text: string) => (
                                                        <StyledH6>
                                                            {text}
                                                        </StyledH6>
                                                    )}
                                                </FormattedMessage>
                                                <CcCartCheck>
                                                    <FormattedMessage
                                                        id={
                                                            isMobile || isTablet
                                                                ? 'restaurant.order.button'
                                                                : 'clickandcollect'
                                                        }>
                                                        {(text: string) => (
                                                            <CcButtonContainer>
                                                                <IconButton
                                                                    borderRadius={
                                                                        10
                                                                    }
                                                                    icon={
                                                                        isMobile ||
                                                                        isTablet ? (
                                                                            <CcButtonIconContainer>
                                                                                <HandIcon />
                                                                            </CcButtonIconContainer>
                                                                        ) : (
                                                                            <Icon
                                                                                code={
                                                                                    tokens.ccHand
                                                                                }
                                                                                marginRight={
                                                                                    4
                                                                                }
                                                                                color={
                                                                                    colors.mayoEggWhite
                                                                                }
                                                                                size={
                                                                                    20
                                                                                }
                                                                            />
                                                                        )
                                                                    }>
                                                                    {text}
                                                                </IconButton>
                                                            </CcButtonContainer>
                                                        )}
                                                    </FormattedMessage>
                                                </CcCartCheck>
                                            </RightBlock>
                                        </FeatureToggle>
                                    )}
                                </RightBlocContainer>
                            </Container>
                        </Placeholder>
                    </ContainerWrapper>
                </Wrapper>

                {sections &&
                    sections.length > 0 &&
                    this.productType() !== productTypes.MULTI_DISHES && (
                        <Fragment>
                            <MenuContainer>
                                {sections.map((section, index) => (
                                    <StyledAnchor
                                        key={index}
                                        onClick={e =>
                                            this.handleTabClick(e, section)
                                        }>
                                        {section.selected ? (
                                            <SelectedTab>
                                                {this.sectionTitle(
                                                    section,
                                                    intl
                                                )}
                                            </SelectedTab>
                                        ) : (
                                            <RightTab key={index}>
                                                {this.sectionTitle(
                                                    section,
                                                    intl
                                                )}
                                            </RightTab>
                                        )}
                                    </StyledAnchor>
                                ))}
                            </MenuContainer>
                            <SectionContainer>
                                {sections.map(
                                    section =>
                                        section.selected &&
                                        section.elements.map(
                                            (element: MenuElement, index) => (
                                                <TooltipPop
                                                    key={index}
                                                    label={element.name}>
                                                    {this.productType() ===
                                                        productTypes.KIDS ||
                                                    this.productType() ===
                                                        productTypes.MULTI_DISHES ? (
                                                        this.renderSectionElement(
                                                            element
                                                        )
                                                    ) : (
                                                        <SectionElementContainer
                                                            onClick={e =>
                                                                this.handleElementClick(
                                                                    e,
                                                                    element,
                                                                    section
                                                                )
                                                            }>
                                                            {this.renderSectionElement(
                                                                element
                                                            )}
                                                            <StyledRadio>
                                                                <Radio
                                                                    checked={
                                                                        element.selected
                                                                    }
                                                                />
                                                            </StyledRadio>
                                                        </SectionElementContainer>
                                                    )}
                                                </TooltipPop>
                                            )
                                        )
                                )}
                            </SectionContainer>
                        </Fragment>
                    )}
                {this.renderIngredients()}
                {!fromEcommerce &&
                    suggestions &&
                    suggestions.products &&
                    suggestions.products.length > 0 && (
                        <FeaturedProducts
                            title={intl.formatMessage({
                                id: 'product.suggestion.title'
                            })}
                            align="left"
                            suggestions={suggestions}
                        />
                    )}
                {!fromEcommerce &&
                    footerNotice &&
                    footerNotice.actionLabel &&
                    footerNotice.actionLabel.length > 0 && (
                        <FooterNotice {...footerNotice} />
                    )}
            </MainContainer>
        )
    }
}
const MainContainer = styled.div``

const BottomBorder = styled.div`
    border-bottom: solid 1px ${grey30};
    padding-bottom: 10px;
    padding-top: 10px;
`

const Divider = styled.div`
    border-bottom: ${BORDER.WIDTH.XS} solid ${grey30};
    width: 100%;
`

const StyledH1 = styled(H1)`
    text-align: center;
    margin: 25px;
    ${media.medium`
        margin: 0px;
        margin-top: 20px;
        line-height: 1;
        font-size: 40px;
        line-height: 45px;
        text-align: left;
    `};
`

const StyledText = styled(SmallText)`
    line-height: 28px;
`

const StyledH6 = styled(H6)`
    margin: 0 auto;
    font-size: 18px;
    text-align: center;
    ${media.medium`
        font-size: 20px;
    `};
`

const Wrapper = styled.div`
    background: ${lightMayo};
    padding-left: 20px;
`

const ContainerWrapper = styled(ResponsiveContainer)`
    padding-top: 50px;
    padding-bottom: 10px;
    ${media.medium`
        padding-top: ${props => (props.fromEcommerce ? '0' : '110px')};
    `};
`

const Container = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    margin: 0 auto;
    ${media.medium`
        padding-top: 60px;
        padding-bottom: 100px;
    `};
`

const SectionContainer = styled(ResponsiveContainer)`
    display: none;
    ${media.medium`
        display: flex;
        align-items: flex-start;
        flex-direction: row;
        white-space: nowrap;
        align-items: center;
        overflow-x: scroll;
    	-webkit-overflow-scrolling: touch;
        ::-webkit-scrollbar {
            display: none;
        }
        position: relative;
        margin: 0 auto;
        padding: ${SPACING.XS} 0;
    `};
`

const MenuContainer = styled(ResponsiveContainer)`
    display: none;
    ${media.medium`
        height: 0px;
        background: white;
        display: flex;
    `};
`

const Tab = css`
    bottom: 45px;
    position: relative;
    height: 45px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding-left: 20px;
    padding-right: 20px;
    background: ${tertiary2};
`

const RightTab = styled.section`
    ${Tab};
    border-radius: 10px 10px 0 0;
`

const SelectedTab = styled.section`
    ${Tab};
    bottom: 56px;
    height: 56px;
    border-radius: 10px 10px 0 0;
    background: white;
`

const MainBlocContainer = styled.div`
    display: none;
    ${media.medium`
        display: flex;
        position: relative;
        margin: auto;
        width: 50%;
        align-items: center;
        top: 30px;
    `};
`

const MenuImageContainer = styled.div`
    display: flex;
    position: relative;
    padding-bottom: 40px;
`

const LeftBlockContainer = styled.section`
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    ${media.medium`
        width: 25%;
    `};
`

const LeftBlock = styled.section`
    display: none;
    ${media.medium`
        margin: auto;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 100%;
    `};
`

const LeftBlockMobile = styled.section`
    margin: auto;
    min-height: 250px;
    width: 100%;
    ${media.medium`
        display: none;
    `};
`

const RightBlocContainer = styled.div`
    width: 100%;
    padding-top: 20px;
    padding-bottom: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    ${media.medium`
        margin: auto;
        width: 25%;
    `};
`

const RightBlock = styled.div`
    padding: 20px;
    background: white;
    border-radius: 10px;
    text-align: center;
    width: 100%;
    ${media.medium`
        width: auto;
    `};
`

const ProductImage = styled.div`
    margin: auto;
    width: 100%;
    ${media.medium`
        width: 90%;
    `};
`

const MenuMainImage = styled.div`
    margin: auto;
    position: absolute;
    top: 50%;
    left: 50%;
    -webkit-transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
    width: 60%;
    z-index: 10;
`

const MenuRightImage = styled.div`
    right: 0;
    position: relative;
    width: 50%;
`

const MenuLeftImage = styled.div`
    position: relative;
    width: 50%;
`

const FlameImage = styled.img`
    position: absolute;
    right: 0;
    float: right;
    padding-right: 20px;
    ${media.medium`
        top: 60px;
        padding-right: 60px;
    `};
`

const Button = css`
    ${style.buttonMedium};
    margin: 15px;
    width: 230px;
    padding: 10px 0;
    display: flex;
    justify-content: center;
    align-items: center;
    ${media.medium`
        width: 255px;
    `};
`

const StyledLink = styled(Link)`
    ${Button}
`

const StyledAnchor = styled.div`
    cursor: pointer;
`

const SectionElementContainer = styled(StyledAnchor)`
    display: flex;
    flex-direction: column;
`

const SectionElement = styled.div`
    min-width: 110px;
    bottom: 0,
    left: 0,
    right: 0,
    position: absolute;
    margin: 10px;
`

const SectionElementMobile = styled.div`
    padding: 5px 10px;
    height: 75px;
    width: 75px;
    margin: auto;
`

const StyledArrow = styled.div`
    position: absolute;
    margin-top: 28px;
`

const MenuTitleContainer = styled.div`
    padding-left: 20px;
`

const StyledRadio = styled.div`
    margin: auto;
    padding: 10px;
    display: flex;
    justify-content: center;
`

const MobileMenu = styled.div`
    margin-top: 3px;
    padding-top: 10px;
    padding-bottom: 5px;
    background: ${tertiary};
    margin-left: -20px;
`

const MobileMenuContainer = styled.div`
    display: flex;
    align-items: flex-start;
    flex-direction: row;
    white-space: nowrap;
    align-items: center;
    overflow-x: scroll;
    -webkit-overflow-scrolling: touch;
    ::-webkit-scrollbar {
        display: none;
    }
`

const CcButtonContainer = styled.div`
    margin: 20px 0;
`

const CcButtonIconContainer = styled.div`
    margin: 0 10px 0 0;
`

const ProductSheetComponent = injectIntl(ProductSheet)

export { ProductSheetComponent }
